import { Camera, CheckCircle2, ImagePlus, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { apiErrorMessage } from '../../api/client'
import { verificationPhotosApi } from '../../api/endpoints'
import type { VerificationPhotosResponse } from '../../types/api'
import { compressImage } from '../../utils/compressImage'
import { AuthedThumb } from '../AuthedImage'
import { ErrorMessage } from '../ErrorMessage'

/**
 * Verification photos for a provider: 3 to 5 photos the Yellow Dot team looks at
 * when verifying them. Renders nothing for anyone else - the server says who is asked.
 */
export function VerificationPhotos({ verificationStatus }: { verificationStatus?: string }) {
  const [data, setData] = useState<VerificationPhotosResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState<string | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    try {
      setData(await verificationPhotosApi.list())
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load your verification photos'))
    }
  }, [])

  // Again when the status changes: verifying locks the photos.
  useEffect(() => {
    void load()
  }, [load, verificationStatus])

  // Nothing to show once verified - the photos were for the check, and it is done.
  if (!data || !data.required || data.locked) return null

  const count = data.photos.length
  const room = data.max - count
  const enough = count >= data.min

  async function onFiles(list: FileList | null) {
    if (!list || list.length === 0 || !data) return
    setError(null)
    const files = Array.from(list).slice(0, room)
    if (list.length > room) {
      setError(`You can add ${room} more photo${room === 1 ? '' : 's'} - the first ${room} were taken.`)
    }
    try {
      for (let i = 0; i < files.length; i++) {
        setUploading(`Uploading ${i + 1} of ${files.length}...`)
        const blob = await compressImage(files[i])
        await verificationPhotosApi.upload(blob, files[i].name.replace(/\.\w+$/, '') + '.jpg')
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'That photo could not be uploaded'))
    } finally {
      setUploading(null)
      if (input.current) input.current.value = ''
      await load()
    }
  }

  async function remove(id: string) {
    setError(null)
    setRemoving(id)
    try {
      await verificationPhotosApi.remove(id)
      await load()
    } catch (err) {
      setError(apiErrorMessage(err, 'That photo could not be removed'))
    } finally {
      setRemoving(null)
    }
  }

  return (
    <section className="card-pad">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-ink-900">
            <Camera size={17} className="text-sun-500" /> Verification photos
          </h2>
          <p className="mt-0.5 text-sm text-ink-500">
            Upload {data.min} to {data.max} clear photos - of you at work, your classroom or your campus. The
            YellowDots team checks them before giving you the Verified badge.
          </p>
        </div>
        <span
          className={`pill shrink-0 ${
            enough ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
          }`}
        >
          {count} of {data.max}
          {enough ? '' : ` (min ${data.min})`}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        {data.photos.map((p, i) => (
          <div key={p.id} className="relative">
            <AuthedThumb
              load={() => verificationPhotosApi.image(p.id)}
              imageKey={p.id}
              alt={`Verification photo ${i + 1}`}
              className="h-28 w-28"
            />
            <button
              type="button"
              onClick={() => void remove(p.id)}
              disabled={removing === p.id}
              aria-label={`Remove photo ${i + 1}`}
              className="absolute -right-2 -top-2 grid h-7 w-7 place-items-center rounded-full border border-ink-200 bg-white text-red-600 shadow-sm hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}

        {room > 0 && (
          <button
            type="button"
            onClick={() => input.current?.click()}
            disabled={!!uploading}
            className="grid h-28 w-28 place-items-center rounded-lg border-2 border-dashed border-ink-300 text-center text-xs font-semibold text-ink-500 transition hover:border-sun-400 hover:text-ink-900 disabled:cursor-wait"
          >
            <span className="flex flex-col items-center gap-1 px-2">
              <ImagePlus size={20} />
              {uploading ?? 'Add photos'}
            </span>
          </button>
        )}
        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => void onFiles(e.target.files)}
        />
      </div>

      <div className="mt-3">
        <ErrorMessage message={error} />
      </div>

      <p className="mt-2 flex items-center gap-1.5 text-sm">
        {enough ? (
          <>
            <CheckCircle2 size={14} className="text-green-600" />
            <span className="text-ink-600">Sent to the YellowDots team for verification.</span>
          </>
        ) : (
          <span className="text-amber-700">
            Add {data.min - count} more photo{data.min - count === 1 ? '' : 's'} to send your verification request.
          </span>
        )}
      </p>
    </section>
  )
}
